import { useEffect, useState } from 'react';
import { api, fmt, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import { useList } from '../lib/useList.ts';
import { camelToSnake, COSTING_DEFAULTS, COSTING_FIELDS, perBoxOf, plyLabel, sheetSizeFor,
         totalOf, type CostingInputs } from '../lib/costing.ts';

/**
 * Every costing Opal has made.
 *
 * Reaching a saved costing used to mean two dropdowns on the sheet -- pick a
 * company, then pick a box -- with no search, no sort and no way to see how
 * many there were. Deleted ones had a table of their own underneath, so the
 * deleted were easier to survey than the live. This is one list of both.
 *
 * The cost per box is worked out here, row by row, with the same
 * `calculateCosting` the sheet uses. It is derived and never stored, so the
 * alternative would be a second copy of the arithmetic written in SQL -- and
 * two copies of a formula is how a quote and a list come to disagree. The
 * price of doing it properly is that cost cannot be sorted or filtered on the
 * server; the stored figures can, and those are what people search by.
 */

interface CostingListRow extends Record<string, unknown> {
  id: number;
  company_name: string;
  box_name: string;
  variant: string;
  status: 'LIVE' | 'DELETED';
  ply: number;
  box_qty: string;
  ups: string;
  updated_at: string;
  deleted_at: string | null;
  change_note: string;
}

/** The row as the maths wants it: every input, numbers as numbers. */
function inputsOf(r: CostingListRow): CostingInputs {
  const out = { ...COSTING_DEFAULTS };
  for (const k of COSTING_FIELDS) {
    const raw = r[camelToSnake(k)];
    if (raw === null || raw === undefined) continue;
    (out as Record<string, unknown>)[k] =
      typeof COSTING_DEFAULTS[k] === 'string' ? String(raw) : Number(raw);
  }
  return out;
}

/**
 * A timestamp as a date. `fmtDate` takes YYYY-MM-DD and hands anything else
 * back untouched, which on a timestamptz means the raw ISO string.
 */
const onDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN',
    { day: '2-digit', month: 'short', year: '2-digit' });

export default function SavedCostings({ user, showing, onOpen, onCount }: {
  user: User;
  /** Showing. The list stays mounted while hidden so it keeps its search. */
  showing: boolean;
  /** Hand a costing to the sheet, which is where one is actually worked on. */
  onOpen: (id: number) => void;
  /** How many there are, for the tab beside it. */
  onCount?: (n: number) => void;
}) {
  /*
   * Live by default.
   *
   * Deleted costings are kept, not destroyed -- the quote one produced may
   * already be with a customer -- but keeping them is not the same as
   * showing them. Listing both by default made deleting look as though it
   * had done nothing, and made the count on the tab claim costings that are
   * not there. They are one filter away instead.
   */
  const list = useList<CostingListRow>('/api/costings/list', 'costings',
                                       { sort: 'updated_at', dir: 'desc',
                                         filters: { status: 'LIVE' } });
  const which = list.state.filters.status ?? '';
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const canDelete = user.permissions?.includes('costing.delete') ?? false;

  // Fresh on the way in, without forgetting what was searched or sorted:
  // a costing saved on the sheet a moment ago should be here, and the
  // filters somebody set should not be thrown away to achieve that.
  const reload = list.reload;
  useEffect(() => { if (showing) void reload(); }, [showing, reload]);
  /*
   * The badge counts live costings, always.
   *
   * It sits next to "Saved costings" on a tab that is read from the sheet as
   * well, so it has to mean one thing. Following whichever filter was last
   * left on would have the sheet announcing a count of deleted ones.
   */
  useEffect(() => {
    if (which === 'LIVE') onCount?.(list.total);
  }, [which, list.total, onCount]);

  async function act(r: CostingListRow, what: 'delete' | 'restore') {
    setBusy(r.id); setError(''); setNotice('');
    try {
      if (what === 'delete') {
        await api.del(`/api/costings/${r.id}`);
        setNotice(`${r.box_name} moved to deleted — it is under Deleted if you want it back.`);
      } else {
        await api.post(`/api/costings/${r.id}/restore`);
        setNotice(`${r.box_name} restored — it is back under Live.`);
      }
      await list.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not ${what} that costing`);
    } finally {
      setBusy(null);
    }
  }

  /*
   * What a list of costings is for: finding one, and comparing it with the
   * others. Every column earns that or it goes.
   *
   * Gone: "Why", which explains one revision and belongs beside that change
   * in the history, not in a column that is empty on nearly every row; and
   * "Ups", which is an input to the arithmetic rather than something anybody
   * scans a list by.
   *
   * Variant rides with the box instead of holding a column of its own, the
   * way the picker names it -- most boxes are costed once, so that column
   * was dashes nearly all the way down.
   *
   * Status appears only when it can differ. Filtered to live, every row
   * would say "Live", and a column of identical badges is furniture.
   */
  const columns: Column<CostingListRow>[] = [
    { key: 'company_name', label: 'Company' },
    { key: 'box_name', label: 'Box',
      render: (r) => (
        <>
          {r.box_name}
          {r.variant && <span className="dim"> · {r.variant}</span>}
        </>
      ) },
    { key: 'ply', label: 'Board', type: 'number', align: 'right',
      render: (r) => plyLabel(r.ply) },
    { key: 'box_qty', label: 'Boxes', type: 'number', align: 'right',
      render: (r) => fmt(r.box_qty) },
    {
      // Derived from the figures saved with the costing, so there is nothing
      // on the server to sort or filter by.
      key: 'per_box', label: 'Per box', align: 'right', sortable: false, filterable: false,
      hint: 'Worked out from the figures saved with this costing, so it is what '
          + 'the box was quoted at — not what it would cost today.',
      render: (r) => {
        const v = perBoxOf(inputsOf(r));
        // A costing missing a factor prices lower than the truth rather than
        // obviously wrong, so a list must withhold it exactly as the sheet does.
        return v === null
          ? <span className="dim" title="Not enough entered to price this">—</span>
          : <strong>₹{v.toFixed(2)}</strong>;
      },
    },
    {
      key: 'job_total', label: 'Job', align: 'right', sortable: false, filterable: false,
      secondary: true, hint: 'The whole run at that cost.',
      render: (r) => {
        const inp = inputsOf(r);
        const v = totalOf(inp, sheetSizeFor(inp.boardHeightIn, inp.boardLengthIn, inp.demyMaxShortIn, inp.demyMaxLongIn) === 'DD'
          ? 'dd' : 'demy');
        return <span className="dim">{v === null ? '—' : `₹${fmt(Math.round(v))}`}</span>;
      },
    },
    { key: 'updated_at', label: 'Last changed', type: 'date',
      render: (r) => <span className="dim">{onDay(r.updated_at)}</span> },
    ...(which === 'LIVE' ? [] : [{
      key: 'status', label: 'Status', type: 'choice' as const,
      choices: [{ value: 'LIVE', label: 'Live' }, { value: 'DELETED', label: 'Deleted' }],
      render: (r: CostingListRow) => (r.status === 'DELETED'
        ? <span className="tag blocked">Deleted</span>
        : <span className="tag ready">Live</span>),
    }]),
    {
      key: 'act', label: '', sortable: false, filterable: false,
      // Shown on the row you are pointing at, or tabbing through. Eight
      // identical Delete buttons made the destructive thing the loudest
      // thing on every line.
      render: (r) => (canDelete ? (
        <button className="btn row-action" disabled={busy === r.id}
                onClick={(e) => { e.stopPropagation(); void act(r, r.status === 'DELETED' ? 'restore' : 'delete'); }}>
          {r.status === 'DELETED' ? 'Restore' : 'Delete'}
        </button>
      ) : null),
    },
  ];

  return (
    <div className="panel">
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      <DataTable
        list={list}
        columns={columns}
        toolbar={(
          /* In the table's toolbar rather than a bar of its own: a second
             full-width strip above the data pushed the first costing a third
             of the way down the screen, for one control. */
          <div className="seg">
            {([['LIVE', 'Live'], ['DELETED', 'Deleted'], ['', 'All']] as const).map(
              ([value, label]) => (
                <button key={label} type="button" aria-current={which === value}
                        onClick={() => list.setFilter('status', value)}>
                  {label}
                </button>
              ),
            )}
          </div>
        )}
        exportName="/api/costings/list"
        viewFilters={['status']}
        rowKey={(r) => r.id}
        /* Deleted costings stay in the list, struck through rather than
           hidden: the quote one produced may already be with a customer. */
        rowClass={(r) => (r.status === 'DELETED' ? 'struck' : '')}
        onRowClick={(r) => onOpen(r.id)}
        empty={which === 'DELETED' ? 'Nothing deleted.'
             : which === 'LIVE' ? 'Nothing costed yet.'
             : 'No costings.'}
      />
    </div>
  );
}
